<script setup>
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import useDashboardState from './composables/useDashboardState';
import DashboardHeader from './components/dashboard/DashboardHeader.vue';
import AllPipelinesView from './components/dashboard/AllPipelinesView.vue';
import SinglePipelineView from './components/dashboard/SinglePipelineView.vue';

const router = useRouter();
const store = useStore();

const {
  // State
  selectedPipelineId,
  dateRange,
  loading,
  error,
  analyticsData,
  singlePipelineMetrics,
  recentActivity,

  // Computed
  isAllPipelinesMode,
  selectedPipeline,
  currentUser,
  dateRangeOptions,
  selectedDateLabel,
  computedDateRange,
  pipelineOptions,
  hasNoAnalyticsData,
  hasNoSinglePipelineData,

  // Methods
  selectPipeline,
  selectDateRange,
  retryLoad,
} = useDashboardState();

// Fase 2.4 — handler do card "Leads esquecidos" navega pro kanban com filtro SLA on
const handleViewForgottenLeads = () => {
  const pipeline = selectedPipeline.value;
  if (!pipeline) return;

  const accountId = store.getters.getCurrentAccountId;
  const slug = `${pipeline.id}-${(pipeline.name || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')}`;

  router.push({
    name: 'pipeline_board',
    params: { accountId, pipelineSlug: slug, view: 'board' },
    query: { sla_exceeded: '1' },
  });
};
</script>

<template>
  <div class="flex flex-col h-full w-full bg-n-background">
    <!-- Header -->
    <DashboardHeader
      :pipeline-options="pipelineOptions"
      :selected-pipeline-id="selectedPipelineId"
      :date-range-options="dateRangeOptions"
      :date-range="dateRange"
      :selected-date-label="selectedDateLabel"
      :is-all-pipelines-mode="isAllPipelinesMode"
      @select-pipeline="selectPipeline"
      @select-date-range="selectDateRange"
    />

    <!-- Scrollable Content Area -->
    <div class="flex-1 overflow-y-auto p-6">
      <!-- Loading State -->
      <div
        v-if="loading"
        class="flex flex-col items-center justify-center h-64"
        role="status"
        aria-live="polite"
      >
        <div
          class="w-10 h-10 border-3 border-n-weak border-t-n-brand rounded-full animate-spin mb-4"
        />
        <p class="text-sm text-n-slate-11">
          {{
            isAllPipelinesMode
              ? $t('PIPELINE.ANALYTICS.LOADING')
              : $t('PIPELINE.DASHBOARD.LOADING')
          }}
        </p>
      </div>

      <!-- Error State -->
      <div
        v-else-if="error"
        class="flex flex-col items-center justify-center gap-4 py-12"
        role="alert"
      >
        <Icon icon="i-lucide-alert-circle" class="size-12 text-n-ruby-9" />
        <div class="text-center">
          <p class="text-base font-medium text-n-slate-12 mb-2">
            {{ $t('PIPELINE.ANALYTICS.ERROR_TITLE') }}
          </p>
          <p class="text-sm text-n-slate-11 mb-4">
            {{ error }}
          </p>
          <Button
            :label="$t('PIPELINE.ANALYTICS.RETRY')"
            size="sm"
            @click="retryLoad"
          />
        </div>
      </div>

      <!-- All Pipelines View (Analytics) -->
      <AllPipelinesView
        v-else-if="isAllPipelinesMode"
        :dashboard-data="analyticsData"
        :has-no-data="hasNoAnalyticsData"
        :date-range="computedDateRange"
      />

      <!-- Single Pipeline View -->
      <SinglePipelineView
        v-else
        :metrics="singlePipelineMetrics"
        :recent-activity="recentActivity"
        :current-user-name="currentUser?.name || ''"
        :has-no-data="hasNoSinglePipelineData"
        @view-forgotten-leads="handleViewForgottenLeads"
      />
    </div>
  </div>
</template>
